import { useQuery } from '@tanstack/react-query'
import type { VoiceView } from '@/shared/api/__generated__'
import { useObjectUrl } from '@/shared/lib'
import { getVoiceClipOptions, getVoiceOptions, listVoicesOptions } from './queries'

export interface VoiceQueryState {
  isPending: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export interface VoiceListFilter {
  /** Только голоса этого аккаунта (`?account=`); пусто — весь каталог. */
  account?: string
}

export function useVoices(filter: VoiceListFilter = {}): VoiceQueryState & { voices: VoiceView[] } {
  const query = useQuery(
    listVoicesOptions(filter.account ? { query: { account: filter.account } } : {}),
  )
  return {
    voices: query.data?.data ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useVoice(voiceId: string): VoiceQueryState & { voice: VoiceView | undefined } {
  const query = useQuery(getVoiceOptions({ path: { id: voiceId } }))
  return {
    voice: query.data?.data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

// Ручка клипа закрыта Bearer'ом — `<audio src>` заголовок не пошлёт, поэтому
// клип качается SDK-вызовом в blob и живёт object-URL'ом до размонтирования.
export function useVoiceClip(voiceId: string): VoiceQueryState & { url: string | null } {
  const query = useQuery({
    ...getVoiceClipOptions({ path: { id: voiceId }, parseAs: 'blob' }),
    // Клип неизменяем: перекачивать его на каждый фокус окна незачем.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })
  const blob = query.data instanceof Blob ? query.data : null

  return {
    url: useObjectUrl(blob),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}
